import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import WaveIcon from '../../../assets/images/wave-icon.svg';
import MicIcon from '../../../assets/images/mic-icon.svg';
import ComputerIcon from '../../../assets/images/computer-icon.svg';
import MusicIcon from '../../../assets/images/megafon-icon.svg';
import VolumeIcon from '../../../assets/images/icon-volume.svg';

export const FeaturesStep = () => {
  const [isMobile, setIsMobile] = useState(() => {
    // Inicializar con detección inmediata de mobile si window existe
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [activeIndex, setActiveIndex] = useState(0); // Empezar con el primer icono activo

  const roles = [
    { name: 'Artist', icon: MicIcon, color: '#FFB447' },
    { name: 'Producer', icon: ComputerIcon, color: '#7C59DE' },
    { name: 'Feat', icon: MusicIcon, color: '#14BD44' },
    { name: 'Listener', icon: VolumeIcon, color: '#C4C9CF' }
  ];

  useEffect(() => {
    let intervalTimer;
    

    const startTimer = setTimeout(() => {
      let currentIndex = 0;
      
      const nextIcon = () => {
        currentIndex = (currentIndex + 1) % roles.length;
        setActiveIndex(currentIndex);
      };
      
      nextIcon(); // Ir al segundo icono (Producer)
      
      // Continuar la secuencia cada 2 segundos
      intervalTimer = setInterval(nextIcon, 2000);
    }, 2500);

    return () => {
      clearTimeout(startTimer);
      if (intervalTimer) clearInterval(intervalTimer);
    };
  }, []);

  // Mobile: Solo contenido animado centrado
  if (isMobile) {
    return (
      <div className="flex flex-col justify-center items-center">
        {/* Wave icon */}
        <motion.img
          src={WaveIcon}
          alt="Wave icon"
          className="w-[195px] h-[145px] mb-6"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ 
            duration: 0.6,
            delay: 0.3,
            ease: "easeOut"
          }}
        />
        
        {/* Role selection horizontal line */}
        <motion.div
          className="flex justify-center items-end gap-10 w-[100%] relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.5,
            delay: 0.9,
            ease: "easeOut"
          }}
          style={{ height: '80px' }}
        >
          {roles.map((role, index) => {
            const isActive = activeIndex === index;
            const iconSize = isActive ? 'w-[29px] h-[29px]' : 'w-[14px] h-[14px]';
            
            return (
              <motion.div
                key={role.name}
                className="flex flex-col items-center"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  duration: 0.4,
                  delay: 1.2 + (index * 0.15),
                  ease: "easeOut"
                }}
              >
                <div
                  className={`flex items-center justify-center rounded-full ${
                    isActive ? 'mb-2' : 'mb-1'
                  }`}
                  style={{
                    backgroundColor: isActive ? role.color : 'transparent',
                    width: isActive ? '50px' : '14px',
                    height: isActive ? '50px' : '14px',
                    transform: isActive ? 'scale(1.2)' : 'scale(1)',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <img 
                    src={role.icon} 
                    alt={`${role.name} icon`} 
                    className={`${iconSize}`}
                    style={{ 
                      filter: isActive ? 'brightness(0) saturate(100%) invert(0%)' : 
                              (index === 0 ? 'brightness(0) saturate(100%) invert(58%) sepia(58%) saturate(348%) hue-rotate(8deg) brightness(106%) contrast(101%)' : 'none'),
                      transition: 'all 0.6s ease-in-out'
                    }}
                  />
                </div>
                <span 
                  className="text-white text-[10.5px] font-archivo"
                  style={{ opacity: isActive ? 1 : 0.7 }}
                >
                  {role.name}
                </span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    );
  }

  // Desktop: Layout 50/50
  return (
    <div className="flex-1 flex px-2">
      {/* Texto del lado izquierdo - 50% */}
      <div className="w-1/2 flex items-center">
        <h1 className="text-white !font-[500] text-[34px] leading-[38px] !font-archivo normal-case">
          Asigná roles a los participantes de tu proyecto
        </h1>
      </div>
      
      {/* Contenedor derecho con animaciones - 50% */}
      <div className="w-1/2 flex flex-col justify-center items-center relative pl-2">
        {/* Wave icon */}
        <motion.img
          src={WaveIcon}
          alt="Wave icon"
          className="w-[149px] h-[105px] mb-8"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ 
            duration: 0.6,
            delay: 0.3,
            ease: "easeOut"
          }}
        />
        
        {/* Role selection horizontal line */}
        <motion.div
          className="flex justify-center items-end gap-6 relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.5,
            delay: 0.9,
            ease: "easeOut"
          }}
          style={{ height: '80px' }}
        >
          {roles.map((role, index) => {
            const isActive = activeIndex === index;
            const iconSize = isActive ? 'w-[29px] h-[29px]' : 'w-[14px] h-[14px]';
            
            return (
              <motion.div
                key={role.name}
                className="flex flex-col items-center"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  duration: 0.4,
                  delay: 1.2 + (index * 0.15),
                  ease: "easeOut"
                }}
              >
                <div
                  className={`flex items-center justify-center rounded-full ${
                    isActive ? 'mb-3' : 'mb-2'
                  }`}
                  style={{
                    backgroundColor: isActive ? role.color : 'transparent',
                    width: isActive ? '52px' : '14px',
                    height: isActive ? '52px' : '14px',
                    transform: isActive ? 'scale(1.3)' : 'scale(1)',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <img 
                    src={role.icon} 
                    alt={`${role.name} icon`} 
                    className={`${iconSize}`}
                    style={{ 
                      filter: isActive ? 'brightness(0) saturate(100%) invert(0%)' : 
                              (index === 0 ? 'brightness(0) saturate(100%) invert(58%) sepia(58%) saturate(348%) hue-rotate(8deg) brightness(106%) contrast(101%)' : 'none'),
                      transition: 'all 0.6s ease-in-out'
                    }}
                  />
                </div>
                <span 
                  className="text-white text-[10.5px] font-archivo"
                  style={{ opacity: isActive ? 1 : 0.7 }}
                >
                  {role.name}
                </span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};